import { PortfolioProperty } from "../types/ownerPortfolio.types";
import {
  formatDaysVacant,
  formatPortfolioDate,
  formatPortfolioMoney,
} from "./formatPortfolioDisplay";

const EMPTY = "—";
const MIN_FRAGMENT_LENGTH = 4;

function fold(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function alreadyShown(container: string | null, fragment: string | null): boolean {
  if (!container || !fragment) {
    return false;
  }

  const needle = fold(fragment);
  if (needle.length < MIN_FRAGMENT_LENGTH) {
    return false;
  }

  return fold(container).includes(needle);
}

export interface PortfolioPlaceLines {
  headline: string;
  condominium: string | null;
  address: string | null;
}

export interface PortfolioPropertyText {
  contract: string;
  place: PortfolioPlaceLines;
  rent: string;
  owner: string;
  unit: string;
  area: string;
  situation: string;
  daysVacant: string | null;
  contractStart: string;
  contractEnd: string;
}

export function getPortfolioPlaceLines(property: PortfolioProperty): PortfolioPlaceLines {
  const title = property.title?.trim() || null;
  const condominium = property.condominium_name?.trim() || null;
  const address = [property.address, property.city].filter(Boolean).join(", ") || null;
  const headline = title || condominium || address || EMPTY;

  return {
    headline,
    condominium:
      condominium && headline !== condominium && !alreadyShown(title, condominium)
        ? condominium
        : null,
    address: address && headline !== address ? address : null,
  };
}

export function getPortfolioPropertyText(property: PortfolioProperty): PortfolioPropertyText {
  return {
    contract: property.contract_code ?? EMPTY,
    place: getPortfolioPlaceLines(property),
    rent: formatPortfolioMoney(property.rent_amount),
    owner: property.owner_name ?? EMPTY,
    unit: property.unit ?? EMPTY,
    area: property.area_display || EMPTY,
    situation: property.situation_label,
    daysVacant: formatDaysVacant(property.days_vacant),
    contractStart: formatPortfolioDate(property.contract_start),
    contractEnd: formatPortfolioDate(property.contract_end),
  };
}
